import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Pencil, Wand2, X, Globe, Layers } from "lucide-react";
import EmptyState from "../../components/EmptyState";
import { metadataGroupsAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { useAgent } from "../../contexts/AgentContext";
import { useCan } from "../../hooks/useCan";
import PageHeader from "../../components/layout/PageHeader";
import Modal from "../../components/ui/Modal";

const EMPTY_ATTR = { name: "", value: "", auto_generate: false, generation_prompt: "" };

function AttributeRow({ attr, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2 py-2 border-b border-gray-50 last:border-0">
      <input
        type="text"
        placeholder="Name"
        value={attr.name}
        onChange={(e) => onChange({ ...attr, name: e.target.value })}
        className="w-1/3 px-2 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
      />
      <input
        type="text"
        placeholder={attr.auto_generate ? "Generation prompt" : "Value"}
        value={attr.auto_generate ? attr.generation_prompt : attr.value}
        onChange={(e) =>
          onChange(
            attr.auto_generate
              ? { ...attr, generation_prompt: e.target.value }
              : { ...attr, value: e.target.value }
          )
        }
        className="flex-1 px-2 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
      />
      <label className="inline-flex items-center gap-1 cursor-pointer select-none flex-shrink-0">
        <input
          type="checkbox"
          checked={attr.auto_generate}
          onChange={(e) => onChange({ ...attr, auto_generate: e.target.checked, value: "", generation_prompt: "" })}
          className="h-3 w-3 rounded text-primary border-input"
        />
        <Wand2 className="h-3 w-3 text-violet-500" />
        <span className="text-xs text-muted-foreground">AI</span>
      </label>
      <button type="button" onClick={onRemove} className="p-1 rounded hover:bg-red-50 flex-shrink-0">
        <X className="h-3.5 w-3.5 text-red-400" />
      </button>
    </div>
  );
}

function GroupModal({ group, onSave, onClose, loading, agentName }) {
  const [name, setName] = useState(group?.name || "");
  const [description, setDescription] = useState(group?.description || "");
  const [attrs, setAttrs] = useState(group?.items || [EMPTY_ATTR]);
  const [isGlobal, setIsGlobal] = useState(group ? (group.is_global ?? group.agent_id === null) : false);

  const canSave = name.trim() && attrs.length > 0 && attrs.every((a) => a.name.trim());

  const handleSave = () => {
    if (!canSave) return;
    onSave({ name: name.trim(), description: description.trim(), items: attrs, is_global: isGlobal });
  };

  const updateAttr = (i, updated) => setAttrs((prev) => prev.map((a, idx) => (idx === i ? updated : a)));
  const removeAttr = (i) => setAttrs((prev) => prev.filter((_, idx) => idx !== i));
  const addAttr = () => setAttrs((prev) => [...prev, { ...EMPTY_ATTR }]);

  return (
    <Modal
      title={group ? "Edit Group" : "New Attribute Group"}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted/50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || loading}
            className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? "Saving\u2026" : "Save Group"}
          </button>
        </>
      }
    >
      <div>
        <label className="block text-xs font-medium text-foreground mb-1">Group Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Premium Customer"
          className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-foreground mb-1">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What scenario context does this group represent?"
          className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>

      {/* Global toggle */}
      <div className="flex items-center gap-3 px-3 py-2.5 bg-muted/50 rounded-lg">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isGlobal}
            onChange={(e) => setIsGlobal(e.target.checked)}
            className="h-4 w-4 rounded text-primary border-input"
          />
          <Globe className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-foreground">Global</span>
        </label>
        <span className="text-xs text-muted-foreground">
          {isGlobal
            ? "Available for all agents"
            : agentName ? `Only for ${agentName}` : "Agent-specific"}
        </span>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-foreground">Attributes *</label>
          <button
            type="button"
            onClick={addAttr}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-primary text-white hover:bg-primary/90"
          >
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
        <div className="bg-muted/50 rounded-lg px-3 py-1 max-h-60 overflow-y-auto">
          {attrs.map((attr, i) => (
            <AttributeRow
              key={i}
              attr={attr}
              onChange={(updated) => updateAttr(i, updated)}
              onRemove={() => removeAttr(i)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}

export default function ConversationAttributesTab() {
  const { currentWorkspace } = useAuth();
  const { selectedAgent } = useAgent();
  const canWrite = useCan("conversation_attribute", "write");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);

  const fetchGroups = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    try {
      setLoading(true);
      const data = await metadataGroupsAPI.getGroups(currentWorkspace.id, selectedAgent?.id);
      setGroups(Array.isArray(data) ? data : data?.data || []);
    } catch (e) {
      console.error("Failed to load groups", e);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, selectedAgent]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      const payload = {
        name: formData.name,
        description: formData.description,
        items: formData.items,
        agent_id: formData.is_global ? null : (selectedAgent?.id || null),
      };
      if (modal?.mode === "edit") {
        await metadataGroupsAPI.updateGroup(modal.group.id, payload, currentWorkspace.id);
      } else {
        await metadataGroupsAPI.createGroup(payload, currentWorkspace.id);
      }
      setModal(null);
      fetchGroups();
    } catch (e) {
      console.error("Failed to save group", e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete "${group.name}"?`)) return;
    try {
      await metadataGroupsAPI.deleteGroup(group.id, currentWorkspace.id);
      fetchGroups();
    } catch (e) {
      console.error("Failed to delete group", e);
    }
  };

  return (
    <div>
      <PageHeader description="Save reusable sets of conversation attributes and apply them to any scenario.">
        {canWrite && (
          <button
            onClick={() => setModal({ mode: "create" })}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Group
          </button>
        )}
      </PageHeader>

      {loading ? (
        <div className="flex justify-center items-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          compact
          icon={Layers}
          title="No Attribute Groups Yet"
          description={selectedAgent?.name
            ? `No groups for ${selectedAgent.name}. Create one or mark existing groups as global.`
            : "Create a group to reuse metadata across scenarios."}
          primaryAction={canWrite ? {
            label: "Create Group",
            onClick: () => setModal({ mode: "create" }),
          } : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {groups.map((group) => (
            <div key={group.id} className="bg-card rounded-xl border border-border p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-foreground truncate">{group.name}</h3>
                    {group.is_global && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">
                        <Globe className="h-3 w-3" /> Global
                      </span>
                    )}
                  </div>
                  {group.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{group.description}</p>
                  )}
                </div>
                {canWrite && (
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => setModal({ mode: "edit", group })}
                      className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => handleDelete(group)}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-1.5">
                {(group.items || []).map((attr, i) => (
                  <span
                    key={i}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      attr.auto_generate
                        ? "bg-violet-50 text-violet-700"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {attr.auto_generate && <Wand2 className="h-2.5 w-2.5" />}
                    {attr.name}
                    {!attr.auto_generate && attr.value ? `=${attr.value}` : ""}
                  </span>
                ))}
              </div>

              <p className="text-xs text-muted-foreground mt-auto">
                {(group.items || []).length} attribute{(group.items || []).length !== 1 ? "s" : ""}
              </p>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <GroupModal
          group={modal.mode === "edit" ? modal.group : null}
          onSave={handleSave}
          onClose={() => setModal(null)}
          loading={saving}
          agentName={selectedAgent?.name}
        />
      )}
    </div>
  );
}
